import type { JobInvoiceStatus } from '../types/job';

export const INVOICE_LIFECYCLES = ['draft', 'sent', 'partially_paid', 'paid', 'voided'] as const;
export type InvoiceLifecycle = (typeof INVOICE_LIFECYCLES)[number];

export const INVOICE_LIFECYCLE_LABELS: Record<InvoiceLifecycle, string> = {
  draft: 'Draft',
  sent: 'Sent',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  voided: 'Voided',
};

export interface LifecycleFacts {
  /**
   * Invoice face value, in integer cents. Never a float.
   *
   * ZERO MEANS UNKNOWN, not free. The backfill creates rows with amount 0 when
   * a legacy job has no breakdown total to price it from (spec §5), so on
   * those rows the money facts cannot decide anything — 0 collected of 0 owed
   * is true of a settled invoice and an open one alike.
   */
  amountCents: number;
  /** Sum of ACTIVE payments, in integer cents. */
  collectedCents: number;
  sentAt: string | null;
  voidedAt: string | null;
  /**
   * When the invoice was recorded as settled — the `paid_at` column.
   *
   * Consulted ONLY when the amount is unknown, as the recorded answer to a
   * question the ledger cannot compute. The backfill stamps it from the job's
   * legacy `paid` status, which is the one place that knows the money arrived
   * even though nobody wrote down how much.
   */
  settledAt: string | null;
}

interface LifecycleRule {
  lifecycle: InvoiceLifecycle;
  ts: (f: LifecycleFacts) => boolean;
  /**
   * The same condition as SQL. Referenced column names must exist in the
   * invoice-ledger CTE (backend/src/services/invoice-ledger.ts), which projects
   * amount_cents and collected_cents alongside the whole invoice row — so
   * paid_at, sent_at and voided_at are in scope too.
   */
  sql: string;
}

/**
 * ORDERED — first match wins. This list is the ONLY definition of the rule.
 * backend/src/services/invoice-ledger.ts emits its CASE expression from it,
 * so the server-side filter and this function cannot disagree (spec D6).
 *
 * `paid` branches on whether the amount is known, because a $0 face value from
 * the backfill means "nobody recorded it", not "nothing is owed". See the rule
 * itself for what each branch is deciding.
 */
export const LIFECYCLE_RULES: readonly LifecycleRule[] = [
  { lifecycle: 'voided',         ts: (f) => f.voidedAt !== null, sql: 'voided_at IS NOT NULL' },
  { lifecycle: 'draft',          ts: (f) => f.sentAt === null,   sql: 'sent_at IS NULL' },
  /*
   * Known amount: the money decides. Unknown amount: the recorded settlement
   * date decides, because there is no sum that could.
   *
   * The previous rule was `amount_cents > 0 AND collected >= amount`, which
   * read every unpriced row as still owed. That is right for one marked sent
   * and wrong for one marked paid, and the two are indistinguishable in the
   * ledger — both are $0 of $0. On prod's backfill it would have put 70
   * already-settled jobs into the Sent stage.
   */
  { lifecycle: 'paid',
    ts: (f) => (f.amountCents > 0 ? f.collectedCents >= f.amountCents : f.settledAt !== null),
    sql: 'CASE WHEN amount_cents > 0 THEN collected_cents >= amount_cents ELSE paid_at IS NOT NULL END' },
  { lifecycle: 'partially_paid', ts: (f) => f.collectedCents > 0, sql: 'collected_cents > 0' },
  { lifecycle: 'sent',           ts: () => true,                 sql: 'TRUE' },
] as const;

export function deriveLifecycle(f: LifecycleFacts): InvoiceLifecycle {
  for (const rule of LIFECYCLE_RULES) if (rule.ts(f)) return rule.lifecycle;
  /* c8 ignore next */
  throw new Error('LIFECYCLE_RULES must end with an unconditional rule');
}

/** Outstanding floors at zero — an overpayment is never negative debt (FR-1.7). */
export function outstandingCents(f: Pick<LifecycleFacts, 'amountCents' | 'collectedCents'>): number {
  return Math.max(0, f.amountCents - f.collectedCents);
}

/** Currency crosses the wire as dollars; all comparison happens in cents (NFR-2). */
export const toCents = (dollars: number | string): number => Math.round(Number(dollars) * 100);
export const toDollars = (cents: number): number => cents / 100;

export const AGING_BUCKETS = ['0-30', '31-60', '61-90', '90+'] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];

/**
 * Upper bound of each bucket, inclusive. Day 30 is '0-30'; day 31 is '31-60'.
 * backend/src/services/invoice-ledger.ts emits its CASE from this same list.
 */
export const AGING_BUCKET_BOUNDS: readonly { max: number | null; bucket: AgingBucket }[] = [
  { max: 30, bucket: '0-30' },
  { max: 60, bucket: '31-60' },
  { max: 90, bucket: '61-90' },
  { max: null, bucket: '90+' },
] as const;

export function agingBucket(daysOutstanding: number | null): AgingBucket | null {
  if (daysOutstanding === null) return null;
  for (const b of AGING_BUCKET_BOUNDS) if (b.max === null || daysOutstanding <= b.max) return b.bucket;
  /* c8 ignore next */
  throw new Error('AGING_BUCKET_BOUNDS must end with an open bucket');
}

// ---------------------------------------------------------------------------
// Per-job billing state (spec §4)
// ---------------------------------------------------------------------------

export const JOB_BILLING_STATES = [
  'not_billed', 'drafted', 'partly_billed', 'sent', 'partly_paid', 'settled',
] as const;
export type JobBillingState = (typeof JOB_BILLING_STATES)[number];

export const JOB_BILLING_STATE_LABELS: Record<JobBillingState, string> = {
  not_billed: 'Not billed',
  drafted: 'Draft invoice',
  partly_billed: 'Partly billed',
  sent: 'Sent',
  partly_paid: 'Partly paid',
  settled: 'Settled',
};

export interface JobBillingFacts {
  /** Invoices on the job that are not voided — drafts included. */
  nonVoidedInvoiceCount: number;
  /** Of those, how many have been sent. A draft is not a bill (spec §4). */
  sentInvoiceCount: number;
  /** A non-voided SENT final exists — the primary completeness signal. */
  hasSentFinal: boolean;
  /** Face value of non-voided SENT invoices, in integer cents. */
  invoicedCents: number;
  /** Active payments against those invoices, in integer cents. */
  collectedCents: number;
  /** From the job's active breakdown document. Null when unknown. */
  contractValueCents: number | null;
}

interface JobBillingRule {
  state: JobBillingState;
  ts: (f: JobBillingFacts) => boolean;
  /**
   * The same condition as SQL. Referenced column names must exist in the
   * job-rollup query (backend/src/services/invoice-ledger.ts), which projects
   * one row per job carrying exactly these six figures.
   */
  sql: string;
}

/**
 * ORDERED — first match wins, same contract as LIFECYCLE_RULES.
 *
 * Billing completeness is checked BEFORE collection: unbilled value is the more
 * actionable fact, so a job that is both partly billed and partly paid reads
 * `partly_billed`. Rules 4-6 are the collection ladder.
 *
 * `partly_billed` is skipped once a sent final exists, because a final IS the
 * everything-else invoice — it defines completeness without depending on the
 * job's breakdown being present or current. The contract-value comparison is
 * only the fallback for jobs billed through partials alone.
 */
export const JOB_BILLING_RULES: readonly JobBillingRule[] = [
  { state: 'not_billed',       ts: (f) => f.nonVoidedInvoiceCount === 0,
                               sql: 'non_voided_count = 0' },
  { state: 'drafted',          ts: (f) => f.sentInvoiceCount === 0,
                               sql: 'sent_count = 0' },
  { state: 'partly_billed',    ts: (f) => !f.hasSentFinal
                                       && f.contractValueCents !== null
                                       && f.invoicedCents < f.contractValueCents,
                               sql: 'NOT has_sent_final AND contract_value_cents IS NOT NULL AND invoiced_cents < contract_value_cents' },
  { state: 'sent',             ts: (f) => f.collectedCents === 0,
                               sql: 'collected_cents = 0' },
  { state: 'partly_paid',      ts: (f) => f.collectedCents < f.invoicedCents,
                               sql: 'collected_cents < invoiced_cents' },
  { state: 'settled',          ts: () => true, sql: 'TRUE' },
] as const;

export function deriveJobBillingState(f: JobBillingFacts): JobBillingState {
  for (const rule of JOB_BILLING_RULES) if (rule.ts(f)) return rule.state;
  /* c8 ignore next */
  throw new Error('JOB_BILLING_RULES must end with an unconditional rule');
}

/**
 * The job's Overview chip — AN ACTION PROMPT, not a money report.
 *
 * This is the distinction that defines the whole function, decided by Stephen
 * on 2026-10-23 after reading a real job on the dev database. Each value tells
 * the office what to DO next:
 *
 *   invoice_sent    something is out and unpaid  -> monitor / chase it
 *   partially_paid  nothing outstanding, but the job is not fully billed
 *                                                -> issue the next invoice
 *   paid            fully billed and fully collected -> nothing to do
 *
 * So the ladder is read in that order, and it is NOT "how much money is in":
 *
 *   1. Is there an unpaid balance on what has already gone out?  -> invoice_sent
 *   2. No, but is there still unbilled value?                    -> partially_paid
 *   3. Neither.                                                  -> paid
 *
 * The consequence that surprises people: a job with money already collected
 * goes BACK to `invoice_sent` when a fresh invoice is issued, because the
 * actionable fact is the new unpaid balance, not the old receipt. An earlier
 * version of this function reported `partially_paid` there — truthful about the
 * money, useless as an instruction, and it hid the fact that a new invoice was
 * waiting on someone.
 *
 * STILL A COARSENING of JOB_BILLING_RULES, never a second rule set: the state
 * decides, and the two cents figures only resolve the cells where a single
 * derived state covers both "something is owed" and "nothing is owed".
 *
 * `null` means LEAVE THE COLUMN ALONE — `not_invoiced` and `ready_to_send` stay
 * purely manual.
 *
 * The money arrives as a named object, not two positional numbers, because
 * swapping `invoicedCents` and `collectedCents` is silent and changes what the
 * office is told to do about a job.
 *
 * NOTE: the `'partially_paid'` returned here is a `JobInvoiceStatus` — the
 * per-JOB column. It is unrelated to the `'partially_paid'` INVOICE_LIFECYCLE
 * above, which is per-INVOICE and does mean "part of the money is in". Same
 * string, two different types, and now two different meanings; do not unify
 * them.
 */
export function autoJobInvoiceStatus(
  state: JobBillingState,
  money: { invoicedCents: number; collectedCents: number },
): JobInvoiceStatus | null {
  const { invoicedCents, collectedCents } = money;

  /*
   * ZERO INVOICED AND ZERO COLLECTED MEANS UNKNOWN, NOT SETTLED — and unknown
   * must never overwrite a human's answer.
   *
   * W1's backfill creates invoices with face value 0 whenever a legacy job had
   * no breakdown total to price it from (Phase 5 spec §5), and synthesises a
   * payment of the same 0 for the ones marked paid. On such a job every money
   * comparison is vacuous: `collected = 0` is true, so JOB_BILLING_RULES
   * derives `sent`, and coarsening that would demote a correctly-settled job to
   * "Invoice Sent".
   *
   * This is the same trap LIFECYCLE_RULES above already disarms by consulting
   * `paid_at` when `amount_cents` is 0 — read the comment on its `paid` rule,
   * which records that the earlier version "would have put 70 already-settled
   * jobs into the Sent stage" on prod's backfill.
   *
   * Narrow on purpose: a 0 face value with REAL money against it is evidence,
   * not silence, so it still falls through.
   */
  if (invoicedCents === 0 && collectedCents === 0) return null;

  switch (state) {
    case 'not_billed':
    case 'drafted':
      return null;

    // `sent` already means collected === 0, and `partly_paid` already means
    // collected < invoiced. Both are "an unpaid balance is out there".
    case 'sent':
    case 'partly_paid':
      return 'invoice_sent';

    // The one state that spans both answers: billing completeness fires here
    // regardless of collection, so the money has to break the tie. Everything
    // sent has been paid -> go bill the rest. Otherwise there is still a
    // balance outstanding and chasing it comes first.
    case 'partly_billed':
      return collectedCents >= invoicedCents ? 'partially_paid' : 'invoice_sent';

    case 'settled':
      return 'paid';
  }
}

/** Money figures a job or client rollup reports, in dollars (FR-1.5). */
export interface BillingRollup {
  contractValue: number | null;
  invoiced: number;
  collected: number;
  outstanding: number;
}
